/**
 * Section: Store Location
 * Live open/closed badge, calculated in the store's timezone from the
 * opening hours blocks (serialized into data-schedule by the section).
 */

(function () {
  'use strict';

  if (customElements.get('store-location-status')) return;

  const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MINUTES_PER_DAY = 1440;

  function formatTime(minutes) {
    const total = minutes % MINUTES_PER_DAY;
    const hour = Math.floor(total / 60);
    const minute = String(total % 60).padStart(2, '0');
    const suffix = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${minute} ${suffix}`;
  }

  /* Expand { from, to, open, close } ranges into seven per-day interval lists */
  function buildWeek(entries) {
    const week = DAY_KEYS.map(() => []);

    entries.forEach((entry) => {
      const from = Number(entry.from);
      const to = Number(entry.to);
      const open = Number(entry.open);
      let close = Number(entry.close);
      if (![from, to, open, close].every(Number.isFinite)) return;
      if (close <= open) close += MINUTES_PER_DAY;

      let day = from;
      for (let i = 0; i < 7; i++) {
        week[day].push({ open, close });
        if (day === to) break;
        day = (day + 1) % 7;
      }
    });

    week.forEach((intervals) => intervals.sort((a, b) => a.open - b.open));
    return week;
  }

  class StoreLocationStatus extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-status-text]');
      if (!this.textEl || !window.Intl) return;

      try {
        this.week = buildWeek(JSON.parse(this.dataset.schedule || '[]'));
        this.formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timezone || 'Asia/Karachi',
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
        });
      } catch (error) {
        return;
      }

      const root = this.closest('.store-location') || this;
      const whenVisible = window.theme?.whenVisible;

      if (whenVisible && root.dataset.visibilityInitialized !== 'true') {
        this.observer = whenVisible(root, () => this.start());
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearInterval(this.timer);
      this.timer = null;
    }

    start() {
      if (this.timer || !this.isConnected) return;
      this.update();
      this.timer = setInterval(() => this.update(), 60000);
    }

    now() {
      const parts = this.formatter.formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value || '';
      return {
        day: DAY_KEYS.indexOf(get('weekday')),
        minutes: (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10),
      };
    }

    currentInterval(day, minutes) {
      const today = this.week[day].find((slot) => minutes >= slot.open && minutes < slot.close);
      if (today) return today;

      const yesterday = this.week[(day + 6) % 7];
      return yesterday.find((slot) => slot.close > MINUTES_PER_DAY && minutes + MINUTES_PER_DAY < slot.close);
    }

    nextOpening(day, minutes) {
      for (let offset = 0; offset < 8; offset++) {
        const target = (day + offset) % 7;
        const slot = this.week[target].find((item) => offset > 0 || item.open > minutes);
        if (!slot) continue;

        const when = offset === 0 ? 'today' : offset === 1 ? 'tomorrow' : DAY_NAMES[target];
        return `${when} at ${formatTime(slot.open)}`;
      }
      return '';
    }

    update() {
      let state;
      try {
        state = this.now();
      } catch (error) {
        return;
      }
      if (state.day < 0 || Number.isNaN(state.minutes)) return;

      const open = this.currentInterval(state.day, state.minutes);

      if (open) {
        this.classList.add('is-open');
        this.textEl.textContent = `Open now · until ${formatTime(open.close)}`;
      } else {
        const next = this.nextOpening(state.day, state.minutes);
        if (!next) return;
        this.classList.remove('is-open');
        this.textEl.textContent = `Closed now · opens ${next}`;
      }

      this.hidden = false;
    }
  }

  customElements.define('store-location-status', StoreLocationStatus);
})();
